import { useCallback } from 'react';

import { useNotification } from '@/components/notification-system';
import type { SectionFailure } from '@/services/association-save';
import { reportBug } from '@/services/bug-report-service';
import { logger } from '@/utils/logger';

export interface SaveFailurePresentation {
	/** Logical flow for bug reports, e.g. 'association-save'. */
	area: string;
	associationId?: string;
	/** The pieces that failed (a fatal core error is passed as one entry). */
	failures: SectionFailure[];
	/** Re-run the whole save. */
	onRetry: () => void;
}

/**
 * Surfaces the terminal save-failure modal once the transport-level retries and
 * the silent reauth have been exhausted. Offers manual Retry and a Report bug
 * action that POSTs full traceability to /bug-reports.
 */
export const useSaveFailureModal = () => {
	const { showModal, showToast } = useNotification();

	return useCallback((p: SaveFailurePresentation) => {
		const { area, associationId, failures, onRetry } = p;
		const lines = failures.map((f) => `• ${f.section}: ${f.error.message}`).join('\n');
		// Permission / bad-input failures aren't transient, so don't imply we kept trying.
		const permanent = failures.length > 0 && failures.every((f) => f.error.kind === 'forbidden' || f.error.kind === 'client');
		const message = permanent
			? `Some settings could not be saved:\n${lines}`
			: `Some settings could not be saved after several attempts:\n${lines}`;

		showModal({
			title: 'Save incomplete',
			message,
			variant: 'error',
			actions: [
				{ label: 'Retry', style: 'default', onPress: onRetry },
				{
					label: 'Report bug',
					style: 'default',
					onPress: async () => {
						try {
							await Promise.all(failures.map((f) => reportBug({
								area,
								section: f.section,
								errorKind: f.error.kind,
								httpStatus: f.error.status,
								message: f.error.message,
								associationId,
							})));
							showToast('Thanks — the issue was reported.', { variant: 'success' });
						} catch (e) {
							logger.error('[bug-report] failed to file from save-failure modal', e);
							showToast('Could not send the report. Please try again later.', { variant: 'error' });
						}
					},
				},
				{ label: 'Dismiss', style: 'cancel', onPress: () => {} },
			],
		});
	}, [showModal, showToast]);
};
